import React, { useState, useEffect } from "react";
import "./ManualPage.css";
import CollapsibleSection from "./CollapsibleSection";

// クリックで拡大する小さなラッパー
function ManualImage({ src, alt, className = "", setLightboxSrc }) {
  return (
    <img
      src={src}
      alt={alt}
      className={`manual-image ${className}`}
      onClick={() => setLightboxSrc(src)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setLightboxSrc(src);
        }
      }}
      aria-label={`${alt}（クリックで拡大）`}
    />
  );
}

const ManualPage = () => {
  const [lightboxSrc, setLightboxSrc] = useState(null);

  // Escキーでライトボックスを閉じる
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightboxSrc(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ★ スクロール演出：.reveal を監視して .reveal--in を付与
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal--in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="manual-page">
      <h1 className="manual-title reveal">AdSche ご利用マニュアル</h1>

      {/* 1. はじめに */}
      <CollapsibleSection title="1. はじめに" defaultOpen={false}>
        <p className="reveal">
          AdSche（アドスケ）は、Amazonスポンサープロダクト広告のON/OFFや予算変更を時間帯別に自動で切り替えられるツールです。<br />
          こんなお悩みがある方には最適です。
        </p>
        <ul className="reveal">
          <li>深夜帯に余計なクリックでの広告費増加にストレスがある方</li>
          <li>時間帯ごとに広告費を設定して効率よく運用したい方</li>
          <li>コスパ重視の運用をしたい方</li>
        </ul>
        <p className="reveal">
          AdScheはバルク操作の一部作業を自動化したツールです。必要最小限でシンプルですが、広告費の節約に大きく貢献します。<br />
          操作も簡単なので、ぜひご活用ください。
        </p>
      </CollapsibleSection>

      {/* 2. 利用開始手順 */}
      <CollapsibleSection title="2. 利用開始手順" defaultOpen={false}>
        <p className="reveal">ツールログイン後、Amazon連携ボタンから広告アカウントと連携 <br />
        ※連携にはセラーセントラルへのログインが必要です。
        </p><br />

        {/* ← ここは ManualImage でOK（is-small はサイズバリエーション） */}
        <ManualImage
          src="/images/collaborate01.jpg"
          alt="登録ステップ"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />
        <ManualImage
          src="/images/collaborate02.jpg"
          alt="登録ステップ"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />
        <ManualImage
          src="/images/collaborate03.jpg"
          alt="登録ステップ"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />
        <ManualImage
          src="/images/collaborate04.jpg"
          alt="登録ステップ"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />
        <p className="reveal">ツール利用の前提条件として、キャンペーンマネージャーにキャンペーンが登録されていることが必要です。<br />
        そのため、登録されていない場合はまずキャンペーンを作成してください。<br />
        ※ツール上でキャンペーン登録は行えません。
        </p><br />
        <ManualImage
          src="/images/preparation01.jpg"
          alt="事前準備"
          className="is-large reveal"
          setLightboxSrc={setLightboxSrc}
        />

      </CollapsibleSection>

      {/* 3. スケジュール設定 */}
      <CollapsibleSection title="3. スケジュール設定方法" defaultOpen={false}>
        <p className="reveal">登録されているキャンペーンに対し、時間帯でON/OFF及び予算変更を行うことができます。<br />
        設定できる項目は下記になります。
        </p>
        <ul className="reveal">
          <li>モード：毎日・1回のみ</li>
          <li>操作：ON・OFF</li>
          <li>予算：対象キャンペーン予算</li>
          <li>時間：スケジュール起動時間※「毎日」設定時</li>
          <li>曜日：スケジュールが稼働する曜日※「毎日」設定時</li>
          <li>実行日時：スケジュール起動年月日※「1回のみ」設定時、過去指定不可</li>
        </ul>

        <p className="reveal">実際の設定は下記のように行ってください。</p>

        <ManualImage
          src="/images/operation01.jpg"
          alt="スケジュール登録画面"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />
         <ManualImage
          src="/images/operation02.jpg"
          alt="スケジュール登録画面"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />
         <ManualImage
          src="/images/operation03.jpg"
          alt="スケジュール登録画面"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />
         <ManualImage
          src="/images/operation04.jpg"
          alt="スケジュール登録画面"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />
         <ManualImage
          src="/images/operation05.jpg"
          alt="スケジュール登録画面"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />
         <ManualImage
          src="/images/operation06.jpg"
          alt="スケジュール登録画面"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />
         <ManualImage
          src="/images/operation07.jpg"
          alt="スケジュール登録画面"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />

        <div className="manual-video reveal">
          <iframe
            src="https://www.youtube.com/embed/5r0GrK1w4RY?si=ltBsCbkWaNqEIjPR"
            title="スケジュール設定動画"
            allowFullScreen
          ></iframe>
        </div>
      </CollapsibleSection>

      {/* 4. CSV更新 */}
      <CollapsibleSection title="4. CSV更新について" defaultOpen={false}>
        <p className="reveal">CSVによる一括新規登録・一括更新ができます。<br />
        登録したいスケジュールの数量が多い場合や、変更したいスケジュールが多い場合は、CSVを利用すると便利です。<br />
        CSVファイルをダウンロードし、編集の上アップロードしてください。<br />
        <br />
        【モード：毎日】<br />
        新規・更新ともに変更する場所はほぼ同じです。<br />
        下記項目を参考にしてください。<br />
        <ul className="reveal">
          <li>scheduleId：変更しない</li>
          <li>campaignId：変更しない</li>
          <li>campaignName：変更しない</li>
          <li>ctargetingType：変更しない</li>
          <li>mode：daily（毎日）</li>
          <li>state：ENABLED（ON） または PAUSED（OFF）</li>
          <li>operation：ON または OFF ※stateと合わせる</li>
          <li>budget：対象キャンペーン予算（100円以上）</li>
          <li>hour：スケジュール実行時間（0～23）</li>
          <li>minute：スケジュール実行時間（0～59）</li>
          <li>weekdays：スケジュール実行曜日（Sun,Mon,Tue,Wed,Thu,Fri,Sat または 月,火,水,木,金,土,日）</li>
          <li>scheduledDateTime：modeがdaily（毎日）の場合は記入しない</li>
          <li>enabled：TRUE（有効） または FALSE（無効）</li>
        </ul>
        </p>
        <ManualImage
          src="/images/csv01.jpg"
          alt="csv更新画面"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />
        <ManualImage
          src="/images/csv02.jpg"
          alt="csv更新画面"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />
        <ManualImage
          src="/images/csv03.jpg"
          alt="csv更新画面"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />

        <p className="reveal">【モード：1回のみ】<br />
        新規・更新ともに変更する場所はほぼ同じです。<br />
        下記項目を参考にしてください。<br />
        <ul className="reveal">
          <li>scheduleId：変更しない</li>
          <li>campaignId：変更しない</li>
          <li>campaignName：変更しない</li>
          <li>ctargetingType：変更しない</li>
          <li>mode：once（1回のみ）</li>
          <li>state：ENABLED（ON） または PAUSED（OFF）</li>
          <li>operation：ON または OFF ※stateと合わせる</li>
          <li>budget：対象キャンペーン予算（100円以上）</li>
          <li>hour：modeがonce（1回のみ）の場合は記入しない</li>
          <li>minute：modeがonce（1回のみ）の場合は記入しない</li>
          <li>weekdays：modeがonce（1回のみ）の場合は記入しない</li>
          <li>scheduledDateTime：実行日時（例：2025/8/20  9:00:00 ←日と時の間は半角スペース2つ）</li>
          <li>enabled：TRUE（有効） または FALSE（無効）</li>
        </ul>
        </p>
        <ManualImage
          src="/images/csv04.jpg"
          alt="csv更新画面"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />
        <ManualImage
          src="/images/csv05.jpg"
          alt="csv更新画面"
          className="is-small reveal"
          setLightboxSrc={setLightboxSrc}
        /><br />

        </CollapsibleSection>


      {/* 5. よくある質問 */}
      <CollapsibleSection title="5. よくある質問（FAQ）" defaultOpen={false}>
        <ul className="reveal">
          <li>
            <strong>Q.</strong> スケジュールを登録してあるはずなのに、一覧に表示されません。<br />
            <strong>A.</strong> 一定時間操作がないと、タイムアウトで表示されないことがあります。<br />
            ログアウトしてから再度ログインしてみてください。
          </li>
          <br />
          <li>
            <strong>Q.</strong> サブスクリプションのキャンセルはどのように行いますか？<br />
            <strong>A.</strong> こちらのマニュアルにある、「6.決済関連・キャンセルについて」をご確認ください。
          </li>
          <br />
          <li>
            <strong>Q.</strong> スケジュールが設定時間ぴったりに反映されていない気がします。<br />
            <strong>A.</strong> 同時刻にスケジュールが大量にある場合、反映まで多少時間を要します。<br />
            他の利用者のスケジュールも同時に実行されるため、数分かかることもあり得ます。<br />
            万が一10分以上経過しても反映されない場合は、コンタクトよりお問い合わせください。
          </li>
          <br />
          <li>
            <strong>Q.</strong> 再利用の場合、過去に登録したスケジュールは残っていますか？<br />
            <strong>A.</strong> 一定期間は残っている可能性がありますが、基本的に削除されると思ってください。
          </li>
          <br />
          <li>
            <strong>Q.</strong> Amazonのセラーアカウントを複数所持していますが、1つのツールで複数管理できますか？<br />
            <strong>A.</strong> 複数管理はできません。AdScheアカウント1つにつきAmazonセラーアカウント1つの利用となります。<br />
            もし複数のセラーアカウントを管理したい場合は、AdScheアカウントを複数作成してください。<br />
            ※サブスクリプションはアカウントごとに発生します。
          </li>
        </ul>
      </CollapsibleSection>

      {/* 6. 決済関連・キャンセルについて */}
      <CollapsibleSection title="6. 決済関連・キャンセルについて" defaultOpen={false}>
        <p className="reveal">
          Stripeでの決済内容確認や、キャンセルはStripeカスタマーポータルにて行えます。
          <br />
          下記ボタンからStripeカスタマーポータルに移動してください。
        </p>

        <ManageBillingButton />
      </CollapsibleSection>

      {/* ライトボックス（画像クリックで開く／背景クリック or Escで閉じる） */}
      {lightboxSrc && (
        <div className="manual-lightbox" onClick={() => setLightboxSrc(null)}>
          <img src={lightboxSrc} alt="拡大表示" />
        </div>
      )}
    </div>
    
  );
};

// ★ カスタマーポータル遷移ボタン
function ManageBillingButton() {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const openPortal = async () => {
    setLoading(true);
    setError("");
    try {
      // どこに保存しているかに合わせて取得（例では3通りを順に試す）
      const fromLS = localStorage.getItem("token");
      const fromSS = sessionStorage.getItem("token");
      const fromCookie = document.cookie
        .split("; ")
        .find((v) => v.startsWith("token="))
        ?.split("=")[1];
      const token = fromLS || fromSS || fromCookie;

      if (!token) {
        throw new Error("ログインが必要です。いったんログインし直してください。");
      }

      const res = await fetch("/api/payment/create-customer-portal-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // ★必須：サーバの authenticateToken が 401 しないように
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `ポータルURLの発行に失敗しました（${res.status}）`);
      }

      const data = await res.json();
      if (!data.url) throw new Error("ポータルURLが取得できませんでした");
      window.location.href = data.url;
    } catch (e) {
      setError(e.message || "エラーが発生しました");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <button
        onClick={openPortal}
        disabled={loading}
        style={{
          padding: "10px 16px",
          borderRadius: 8,
          border: "none",
          background: "#0ea5e9",
          color: "#fff",
          fontWeight: 600,
          cursor: loading ? "not-allowed" : "pointer"
        }}
      >
        {loading ? "開いています..." : "請求を管理（カスタマーポータル）"}
      </button>
      {error && <div style={{ color: "#ef4444", marginTop: 8 }}>{error}</div>}
    </div>
  );
}

export default ManualPage;